/**
 * DeskFindingsListMolecule — the Desk's "Needs attention" region: a kind-filterable list of
 * `DeskFinding`s.
 *
 * Pre-create check (atomic-design Rule 3): no existing list molecule models a
 * severity-carrying, kind-filterable finding set. The filter row reuses the existing `TagAtom`
 * (its `pressed` prop, unmodified) rather than forking a toggle; empty/degraded states reuse
 * `EmptyStateAtom` / `MessageAtom`. Only the filter+group composition is new.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { TagAtom, EmptyStateAtom, MessageAtom } from 'design-system/my-lib';
import type { DeskFinding, DeskFindingKind } from 'types/studio';
import DeskFindingRowMolecule from './DeskFindingRowMolecule';
import './DeskFindingsListMolecule.scss';

export interface DeskFindingsListMoleculeProps {
  findings: DeskFinding[];
  /** True when the `findings` sub-source failed entirely. */
  degraded?: boolean;
  /** Present when the publish-blocked scan was bounded — the header names how many drafts were skipped. */
  truncated?: number;
  onOpen: (finding: DeskFinding, route: string) => void;
  className?: string;
}

const ALL_KINDS: DeskFindingKind[] = ['stale_draft', 'publish_blocked', 'semantic_collision'];

/**
 * Needs-attention region (spec SC5/UI Components table) — kind filter chips over the three
 * finding kinds, filtering client-side (the desk call already fetched every kind by default); an
 * empty inbox after filtering renders a real empty state, never a fabricated zero.
 */
const DeskFindingsListMolecule: React.FC<DeskFindingsListMoleculeProps> = ({
  findings,
  degraded = false,
  truncated,
  onOpen,
  className = '',
}) => {
  const { t } = useTranslation();
  // `null` = every kind shown (the default). Selecting a kind isolates the list to it; selecting
  // the same kind again returns to "every kind" — a single-select filter, not a multi-select one,
  // since the three kinds are already grouped visually by severity and stacking filters adds
  // little over picking one at a time.
  const [onlyKind, setOnlyKind] = useState<DeskFindingKind | null>(null);

  const toggleKind = (kind: DeskFindingKind) => {
    setOnlyKind((prev) => (prev === kind ? null : kind));
  };

  const visible = useMemo(
    () => (onlyKind ? findings.filter((finding) => finding.kind === onlyKind) : findings),
    [findings, onlyKind]
  );

  return (
    <div className={['desk-findings-list', className].filter(Boolean).join(' ')} data-testid="desk-findings-list">
      <div
        className="desk-findings-list__filters"
        role="toolbar"
        aria-label={t('studio.desk.findings.filterLabel')}
      >
        {ALL_KINDS.map((kind) => (
          <TagAtom
            key={kind}
            label={t(`studio.desk.findings.kind.${kind}`)}
            pressed={onlyKind === kind}
            size="small"
            data-testid={`desk-findings-filter-${kind}`}
            onClick={() => toggleKind(kind)}
          />
        ))}
      </div>

      {degraded && (
        <div data-testid="desk-findings-degraded">
          <MessageAtom
            severity="warn"
            text={
              truncated
                ? t('studio.desk.findings.degradedTruncated', { count: truncated })
                : t('studio.desk.findings.degraded')
            }
          />
        </div>
      )}

      {visible.length === 0 && !degraded && (
        <EmptyStateAtom
          icon="pi-check-circle"
          title={t('studio.desk.findings.emptyTitle')}
          description={t('studio.desk.findings.emptyDescription')}
        />
      )}

      {visible.length > 0 && (
        <ul className="desk-findings-list__items">
          {visible.map((finding) => (
            <DeskFindingRowMolecule key={finding.id} finding={finding} onOpen={onOpen} />
          ))}
        </ul>
      )}
    </div>
  );
};

export default DeskFindingsListMolecule;
